// ============================================================
// Analytics v2 — signing, encryption and stateless tokens
//
//   signPayload / verifySignature  Stripe-style `t=<unix>,v1=<hex>` HMAC
//                                  (server ingest + outgoing webhooks)
//   encryptSecret / decryptSecret  AES-GCM for secrets at rest
//   mintEmbedToken / verifyEmbedToken  short-lived, sub-tenant-scoped report access
// ============================================================
const enc = new TextEncoder();
const dec = new TextDecoder();
export const SIGNATURE_TOLERANCE_SECONDS = 300;
function hex(buf) {
    return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, '0')).join('');
}
export function toBase64Url(bytes) {
    let bin = '';
    for (const b of bytes) {
        bin += String.fromCharCode(b);
    }
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export function fromBase64Url(s) {
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
        out[i] = bin.charCodeAt(i);
    }
    return out;
}
async function hmacKey(secret) {
    return crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
}
export async function hmacSha256Hex(secret, data) {
    return hex(await crypto.subtle.sign('HMAC', await hmacKey(secret), enc.encode(data)));
}
export function timingSafeEqual(a, b) {
    if (a.length !== b.length) {
        return false;
    }
    let diff = 0;
    for (let i = 0; i < a.length; i++) {
        diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }
    return diff === 0;
}
/** `t=<unix>,v1=<hex hmac_sha256(secret, "<t>.<payload>")>` */
export async function signPayload(secret, payload, nowSeconds = Math.floor(Date.now() / 1000)) {
    return `t=${nowSeconds},v1=${await hmacSha256Hex(secret, `${nowSeconds}.${payload}`)}`;
}
export async function verifySignature(secret, header, payload, nowSeconds = Math.floor(Date.now() / 1000), toleranceSeconds = SIGNATURE_TOLERANCE_SECONDS) {
    if (!secret || !header) {
        return false;
    }
    const parts = {};
    for (const kv of header.split(',')) {
        const i = kv.indexOf('=');
        if (i <= 0) {
            continue;
        }
        const k = kv.slice(0, i).trim();
        (parts[k] ??= []).push(kv.slice(i + 1).trim());
    }
    const t = Number(parts.t?.[0]);
    if (!Number.isFinite(t) || Math.abs(nowSeconds - t) > toleranceSeconds || !parts.v1) {
        return false;
    }
    const expected = await hmacSha256Hex(secret, `${t}.${payload}`);
    // Several v1 entries are allowed (secret rotation).
    return parts.v1.some(sig => timingSafeEqual(sig, expected));
}
// --- AES-GCM secret encryption (key = SHA-256 of the env secret) ---
async function aesKey(secret) {
    const raw = await crypto.subtle.digest('SHA-256', enc.encode(secret));
    return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}
/** Returns `v1.<iv>.<ciphertext>` (base64url). */
export async function encryptSecret(plaintext, keySecret) {
    if (!keySecret) {
        throw new Error('encryption key not configured');
    }
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, await aesKey(keySecret), enc.encode(plaintext));
    return `v1.${toBase64Url(iv)}.${toBase64Url(new Uint8Array(ct))}`;
}
export async function decryptSecret(sealed, keySecret) {
    const [v, iv, ct] = sealed.split('.');
    if (v !== 'v1' || !iv || !ct) {
        throw new Error('unsupported sealed secret');
    }
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromBase64Url(iv) }, await aesKey(keySecret), fromBase64Url(ct));
    return dec.decode(pt);
}
export function randomSecret(prefix, bytes = 24) {
    return `${prefix}${toBase64Url(crypto.getRandomValues(new Uint8Array(bytes)))}`;
}
// --- Embed tokens ---
export const EMBED_TOKEN_PREFIX = 'yom_et_';
export const EMBED_TOKEN_MAX_TTL_SECONDS = 24 * 60 * 60;
/** Embed tokens a partner mints for a child project live 15 minutes at most (docs/partner-api.md). */
export const PARTNER_EMBED_TOKEN_MAX_TTL_SECONDS = 900;
/** jti prefix of partner-minted tokens (routes/v2/partner.ts). */
export const PARTNER_EMBED_JTI_PREFIX = 'etj_';
/** jti prefix of project-minted tokens (routes/v2/manage.ts). */
export const PROJECT_EMBED_JTI_PREFIX = 'etp_';
export const isPartnerEmbedJti = (jti) => jti.startsWith(PARTNER_EMBED_JTI_PREFIX);
const PROJECT_JTI_RE = /^etp_([a-f0-9]{24})([a-f0-9]{16})$/;
const projectJtiMac = async (secret, projectId, nonce) => (await hmacSha256Hex(secret, `etp:${projectId}:${nonce}`)).slice(0, 16);
/**
 * A project token id: random nonce + a MAC of the project id. The denylist is keyed by jti
 * alone, so the MAC is what stops an org from pre-revoking (and so pinning) the id of a
 * token another org published: a jti can only be revoked on its own project.
 */
export async function mintProjectEmbedJti(secret, projectId) {
    const nonce = crypto.randomUUID().replace(/-/g, '').slice(0, 24);
    return `${PROJECT_EMBED_JTI_PREFIX}${nonce}${await projectJtiMac(secret, projectId, nonce)}`;
}
/** True when `jti` was minted for `projectId` (mintProjectEmbedJti). */
export async function projectEmbedJtiValid(secret, projectId, jti) {
    const m = PROJECT_JTI_RE.exec(jti);
    return !!secret && !!m && timingSafeEqual(m[2], await projectJtiMac(secret, projectId, m[1]));
}
export async function mintEmbedToken(claims, secret) {
    if (!secret) {
        throw new Error('EMBED_TOKEN_SECRET not configured');
    }
    const body = toBase64Url(enc.encode(JSON.stringify(claims)));
    return `${EMBED_TOKEN_PREFIX}${body}.${await hmacSha256Hex(secret, body)}`;
}
export async function verifyEmbedToken(token, secret, nowSeconds = Math.floor(Date.now() / 1000)) {
    if (!secret || !token.startsWith(EMBED_TOKEN_PREFIX)) {
        return null;
    }
    const rest = token.slice(EMBED_TOKEN_PREFIX.length);
    const dot = rest.lastIndexOf('.');
    if (dot <= 0) {
        return null;
    }
    const body = rest.slice(0, dot);
    const sig = rest.slice(dot + 1);
    if (!timingSafeEqual(sig, await hmacSha256Hex(secret, body))) {
        return null;
    }
    try {
        const claims = JSON.parse(dec.decode(fromBase64Url(body)));
        if (typeof claims.exp !== 'number' || claims.exp < nowSeconds) {
            return null;
        }
        if (typeof claims.p !== 'string' || typeof claims.t !== 'string') {
            return null;
        }
        return {
            p: claims.p,
            t: claims.t,
            st: typeof claims.st === 'string' ? claims.st : '',
            r: Array.isArray(claims.r) ? claims.r : [],
            o: Array.isArray(claims.o) ? claims.o : [],
            exp: claims.exp,
            ...(typeof claims.j === 'string' && claims.j ? { j: claims.j } : {}),
        };
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=crypto.js.map